














































































import Vue from 'vue'
import { mapGetters } from 'vuex'
import mixin from '@/mixins/message-mixin'
import MessagePreviewBubble from '@/components/MessagePreviewBubble.vue'
import ScheduleBuilder from '@/components/ScheduleBuilder.vue'
export default Vue.extend({
    mixins: [mixin],
    components: { MessagePreviewBubble, ScheduleBuilder },
    props: {
        //  The broadcast being previewed
        broadcast: {
            type: Object,
            required: true
        },
        companyToggle: {
            type: Boolean,
            default: false
        },
        consentToggle: {
            type: Boolean,
            default: false
        },
        //  We pass the boolean checks from BroadcastForm.vue to see if consent is already in the message to reduce repeat code and prevent double consent appearing in the message
        hasConsent: {
            type: Boolean, 
            default: true
        },
        //  We pass the boolean checks from BroadcastForm.vue to see if company name is already in the message to reduce repeat code and prevent double company name appearing in the message
        hasCompanyName: {
            type: Boolean, 
            default: true
        }
    },
    data: () => ({
        loading: false,
        media: null,
        //  Indicates if the schedules attached to the broadcast are valid
        schedulesValid: false
    }),
    async mounted() {
        //  We want to store media separately because we will remove it later
        this.media = this.broadcast.media
    },
    computed: {
        ...mapGetters([
            'getEnabled',
            'getLimit',
            'isApp',
            'plan',
            'stripeCustomerHasPayment',
            'subscription',
            'tenant',
            'trialing'
        ]),
        a2pEnabled(): boolean {
            //  Check if A2P is enabled
            return this.getEnabled('a2p')
        },
        broadcastLimit(): number {
            //  Grab our limits
            return this.getLimit('broadcast') || 250
        },
        broadcastLimitReached(): boolean {
            //  If a2p is enabled, return false because we have no limits
            if (this.a2pEnabled) return false

            //  Else check the persons count
            const count = this.contactsCount

            //  Check if the count exceeds current limit
            return count > this.broadcastLimit
        },
        broadcastMessage(): string {
            //  Grab our broadcast message
            let msg = this.broadcast.message || ''

            //  If we have our company toggled and it doesn't have a company name yet we append it, this logic prevents a case where a draft or scheduled broadcast when editied appends additonal
            //  company names over and over again for each edit
            if (this.companyToggle && !this.hasCompanyName) {
                msg = `${this.companyName}${msg}`
            }
            //  If we have our consent message toggled and don't have our consent message in the broadcast, append it, same reasoning as above
            if (this.consentToggle && !this.hasConsent) {
                msg = `${msg}${this.consentMsg}`
            }

            //  In the case the user is editing their message from a schedule or draft broadcast we need the inverse logic of above so that the broadcast preview removes the text if they
            //  turn one of the toggles off
            if (!this.companyToggle && this.hasCompanyName) {
                msg = msg.replace(this.companyName, '')
            }
            if (!this.consentToggle && this.hasConsent) {
                msg = msg.replace(this.consentMsg, '')
            }

            //  Return our message
            return msg
        },
        companyName(): string {
            //  Grab the current tenant from the store
            const tenant = this.$store.getters.tenant

            //  Get our tenant's organization name
            const orgName = tenant.company_name

            //  Return the orgname with a newline appended
            return `${orgName}\n`
        },
        consentMsg(): string {
            //  Grab our consent from the store
            const consent = this.$store.getters.consent

            //  Grab the opt in message
            const optIn = consent?.opt_in_message

            //  Return the opt in with a newline prepended
            return `\n${optIn}`
        },
        contactsCount(): number {
            return this.$store.getters.broadcastsViewSelectedBroadcastTotalCount
        },
        //  Determine the credit cost to send the message
        creditCount(): number {
            //  If the message count is greater than 3, we return default 3
            if (this.messageCount > 3) return 3

            //  Else, if the message contains media (ie is MMS), we return default 3
            if (this.media) return 3

            //  Else, return the message count (will be less than 3)
            return this.messageCount
        },
        creditLimitReached(): boolean {
            //  Get our broadcast text
            const text = this.broadcast.message || ''

            //  Get our message length
            const length = text.length

            //  Get our message char limit
            const charLimit = this.messageCharLimit(text)

            //  Find out how many messages are going to send
            const msgCount = Math.ceil(length / charLimit)

            //  If the message contains MMS, multiply msgCount by 3
            let count = this.broadcast.media ? msgCount * 3 : msgCount

            //  Find out how many persons this is being sent to
            const personCount = this.contactsCount

            //  If count is 0 (message is empty and user is trying to send image)
            if (count == 0 && this.broadcast.media) {
                //  count equals personsCount multiplied by 3 for an image
                count = personCount * 3
            }

            //  Get our total count
            const totalCount = count * personCount

            //  Get our total credits
            const credits = this.tenant.credits

            //  Else, check if we are trialing
            const trialing = this.subscription.status === 'trialing'

            //  If the total count is greater than our current credits AND we are on default starting plan, return true
            if (totalCount > credits && trialing) return true

            //  Else return false
            return false
        },
        // Calculates the estimated time for broadcast send
        estimatedTime(): string {
            // Calculate seconds to send broadcast
            const secondsToSend = 0.5 * this.contactsCount

            // If less than a minute, display seconds
            if (secondsToSend < 60)
                return secondsToSend.toString().substr(0, 3) + ' seconds'

            // Calculate minutes to send broadcast, rounded to the next minute just to be safe
            const minutesToSend = Math.ceil(secondsToSend / 60)

            // If less that an hour, display minutes
            if (minutesToSend < 60) return minutesToSend.toString() + ' minutes'

            // Else, calculate hours rounded to the next hour just to be safe
            const hoursToSend = Math.ceil(minutesToSend / 60)

            // Return hours it will take to send broadcast
            return hoursToSend.toString() + ' hours'
        },
        loadingCount(): boolean {
            return this.$store.getters.broadcastsViewSelectedBroadcastLoadingCount
        },
        //  Determine the number of message parts to send the current text
        messageCount(): number {
            //  If this broadcast contains media, it is always 3
            if (this.broadcast.media_id) return 3

            //  Grab our broadcast text
            const text = this.broadcast.message

            //  Get our character limit
            const charLimit = this.messageCharLimit(text)

            //  Calculate how many messages to send the text
            const count = Math.ceil(text.length / charLimit)

            //  If the count is greater than zero, return the count
            if (count > 0) return count

            //  Else, return the default 1 (even if no text exists)
            return 1
        },
        //  Indicates if the 'scheduling' feature is enabled for this tenant & user
        schedulingEnabled(): boolean {
            //  Check if subscription plan allows it
            return this.getEnabled('scheduling')
        },
        scheduledNextSendBroadcast(): string {
            // const date = new Date(this.broadcast.schedule[0].next_send)
            // return date.toLocaleDateString()
            return ''
        },
        sendCopy(): string {
            //  If no schedules, tell them to send immediately
            if (!this.broadcast.schedule) return `start sending <b>immediately</b>`
            //  Else this is a schedules broadcast then return the schedueld copy
            // return `send on ${this.scheduledNextSendBroadcast}`
            return ''
        },
        //  Determine if the message will be sent as an MMS
        showMMS(): boolean {
            //  If the SmsAccount has not been loaded yet, return false (this occurs when switching between tenants)
            if (!this.$store.getters.smsAccount) return false

            //  Check if tenant has a message provider that offers MMS
            const mmsAvailable = this.$store.getters.smsAccount.provider === 'bandwidth'

            //  Determine if the message will be sent as an MMS (ie its available AND the message count if greater than 3)
            const showMMS = mmsAvailable && this.messageCount > 3

            // Return the result
            return showMMS
        },
        totalCreditUsage(): number {
            return this.contactsCount * this.creditCount
        }
    },
    methods: {
        async broadcastCreate(): Promise<void> {
            //  If the persons count exceeds their limit, stop and inform them
            if (this.broadcastLimitReached) return this.popBroadcastLimitReached()

            //  If the user doesn't have access to scheduling messages, pop disabled dialog
            if (this.broadcast.schedule && !this.schedulingEnabled)
                return this.popSchedulingDisabled()

            //  If the tenant doesn't have enough credits, stop and inform them
            if (this.creditLimitReached) return this.popCreditLimitReached()

            //  Start the loading spinner
            this.loading = true

            //  Prep our broadcast
            this.prepBroadcastPost()

            //  Set status to null, done for broadcast drafts since
            //  it is now being sent out
            if (!this.broadcast.schedule) this.broadcast.status = null

            //  Send the request
            const response = await this.$store.dispatch('broadcastsPost', {
                ...this.broadcast,
                message: this.broadcastMessage
            })

            //  Stop the loading spinner
            this.loading = false

            //  If the request failed, do nothing else
            if (!response) return

            //  Close the preview
            this.$store.dispatch('dialogClose', 'APP')

            //  Notify the user of success
            this.$store.commit('appNotificationBarSet', {
                message: `Broadcast ${this.broadcast.schedule ? 'scheduled' : 'sent'}!`
            })

            //  Navigate to the broadcasts index
            this.$router.push('/broadcasts')
        },
        popBroadcastLimitReached(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'FEATURE',
                paywall_type: 'limit',
                metadata: 'broadcast_limit'
            })
        },
        popCreditLimitReached(): void {
            //  Get the current plan
            const plan = this.plan

            //  Grab the index from the metadata
            const index = plan.metadata.index

            //  Pop the dialog
            this.$store.dispatch('dialogOpen', {
                type: this.isApp ? 'MESSAGE' : 'CONFIRM',
                title: 'Credit Limit Reached',
                text: 'You are attempting to send a broadcast that is above your message credit limit or contact per broadcast limit. You can update your current subscription to avoid this or you can edit your broadcast message accordingly.',
                icon: 'mdi-alert',
                color: 'yellow',
                hideNo: true,
                yesText: this.isApp ? null : 'Manage Plan',
                closeable: true,
                closeOnSubmit: true,
                callback: (response) => {
                    //  If the user answered 'No' in the dialog OR we're on a mobile device, do nothing
                    if (!response || this.isApp) return

                    //  Navigate to the current subscription plan
                    this.$router.push(`/billing/manage?index=${index}`)

                    //  Close the dialogs
                    this.$store.dispatch('dialogClose', 'APP')
                }
            })
        },
        prepBroadcastPost(): void {
            //  Grab our media id
            const media_id = (this.broadcast.media && this.broadcast.media.id) || null

            //  Remove media from the broadcast
            delete this.broadcast.media

            //  Attach media id
            this.broadcast.media_id = media_id
        },

        // Reset schedules attached to the message
        resetSchedules(): void {
            //  Set the message's schedules to null
            this.broadcast.schedule = null

            //  Force update the UI
            this.$nextTick(() => this.$forceUpdate())
        },
        validateSchedules(): boolean {
            //  If schedules do NOT exist, they're not valid, return false
            if (!this.broadcast.schedule) return false

            //  Create an errors container
            const errors = []

            //  Grab our current schedule to check
            const schedule = this.broadcast.schedule

            //  Search for errors in the schedules array
            const v = schedule.next_send !== null && schedule.interval !== null
            if (!v) errors.push(schedule)

            //  Check if errors were found
            const valid = errors.length === 0

            //  Update the validation state with the result
            this.schedulesValid = valid
        }
    }
})
